import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as authApi from "./authApi";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [ninaAccess, setNinaAccess] = useState(null);
  const [ninaAccessLoading, setNinaAccessLoading] = useState(true);
  const [ninaAccessError, setNinaAccessError] = useState("");

  const refreshNinaAccess = useCallback(async () => {
    setNinaAccessLoading(true);
    setNinaAccessError("");

    try {
      const result = await authApi.getNinaAccessContext();
      setNinaAccess(result?.access || null);
      return result;
    } catch (error) {
      setNinaAccess(null);
      setNinaAccessError(
        error?.message || "NINA account access is unavailable."
      );
      return null;
    } finally {
      setNinaAccessLoading(false);
    }
  }, []);

  const clearNinaAccess = useCallback(() => {
    setNinaAccess(null);
    setNinaAccessLoading(false);
    setNinaAccessError("");
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const result = await authApi.getSession();
      const authenticatedUser = result?.authenticated ? result.user : null;

      setUser(authenticatedUser);

      if (authenticatedUser) {
        await refreshNinaAccess();
      } else {
        clearNinaAccess();
      }

      return result;
    } finally {
      setLoading(false);
    }
  }, [clearNinaAccess, refreshNinaAccess]);

  useEffect(() => {
    refreshSession().catch(() => {
      setUser(null);
      clearNinaAccess();
      setLoading(false);
    });
  }, [clearNinaAccess, refreshSession]);

  const login = async (email, password) => {
    const result = await authApi.signIn(email, password);
    setUser(result.user);
    setMessage("");
    await refreshNinaAccess();
    return result;
  };

  const register = async (email, password) => {
    const result = await authApi.signUp(email, password);
    const authenticatedUser = result.authenticated ? result.user : null;

    setUser(authenticatedUser);
    setMessage(result.message || "");

    if (authenticatedUser) {
      await refreshNinaAccess();
    } else {
      clearNinaAccess();
    }

    return result;
  };

  const logout = async () => {
    try {
      await authApi.signOut();
    } finally {
      setUser(null);
      setMessage("");
      clearNinaAccess();
    }
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      message,
      ninaAccess,
      ninaAccessLoading,
      ninaAccessError,
      isAdmin: user?.role === "admin" || user?.role === "owner",
      isOwner: user?.role === "owner",
      login,
      register,
      logout,
      refreshSession,
      refreshNinaAccess,
      requestPasswordReset: authApi.requestPasswordReset,
      establishRecoverySession: async (accessToken, refreshToken) => {
        const result = await authApi.establishRecoverySession(
          accessToken,
          refreshToken
        );
        setUser(result.user);
        await refreshNinaAccess();
        return result;
      },
      updatePassword: async (password) => {
        const result = await authApi.updatePassword(password);
        setUser(result.user);
        await refreshNinaAccess();
        return result;
      },
      getAdminStatus: authApi.getAdminStatus,
      getOwnerStatus: authApi.getOwnerStatus,
      getOwnerOverview: authApi.getOwnerOverview,
      grantComplimentaryAccess: authApi.grantComplimentaryAccess,
      revokeComplimentaryAccess: authApi.revokeComplimentaryAccess,
    }),
    [
      user,
      loading,
      message,
      ninaAccess,
      ninaAccessLoading,
      ninaAccessError,
      refreshSession,
      refreshNinaAccess,
      clearNinaAccess,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
